import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { academyPublicAPI } from '../../lib/academyApi';
import {
  ArrowRight, Clock, Users, MapPin, Monitor, Loader2,
  BookOpen, GraduationCap, Building2, Target, ChevronRight,
  Zap, Award, Video,
} from 'lucide-react';

const AcademieBrand = ({ size = 'lg' }) => {
  const cls = size === 'xl' ? 'text-5xl sm:text-6xl lg:text-7xl' : size === 'lg' ? 'text-3xl sm:text-4xl' : 'text-xl';
  return (
    <span className={`font-heading font-bold ${cls}`}>
      PEY <span className="text-white">a</span>
      <span style={{ color: '#FFD537' }}>CAD</span>
      <span className="text-white">emie</span>
    </span>
  );
};

const modeLabel = { presentiel: 'Presentiel', distance: 'A distance', hybride: 'Hybride' };
const modeIcon = { presentiel: MapPin, distance: Monitor, hybride: Users };

const AcademyPage = () => {
  const [settings, setSettings] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const [s, t, c, i] = await Promise.all([
          academyPublicAPI.getSettings(),
          academyPublicAPI.getTrainings(),
          academyPublicAPI.getCategories(),
          academyPublicAPI.getInstructors(),
        ]);
        setSettings(s.data);
        setTrainings(t.data || []);
        setCategories(c.data || []);
        setInstructors(i.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" /></div>;

  const filtered = filter === 'all' ? trainings : trainings.filter(t => t.category_id === filter);

  return (
    <div data-testid="academy-page">
      {/* Hero */}
      <section className="relative bg-[#1D1D1B] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'url("https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1920&q=30")',backgroundSize:'cover',backgroundPosition:'center'}} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1D1D1B] via-[#1D1D1B]/95 to-[#1D1D1B]/80" />
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <div className="mb-6">
              <AcademieBrand size="xl" />
            </div>
            <p className="text-xl sm:text-2xl text-white/70 mb-8 leading-relaxed max-w-2xl">
              {settings?.hero_subtitle || 'La plateforme de formation professionnelle de PEY Consultants'}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#catalogue">
                <Button size="lg" className="text-base px-8 h-14" data-testid="academy-cta-btn">
                  <GraduationCap className="w-5 h-5 mr-2" />
                  Explorer les formations
                </Button>
              </a>
              <a href="#entreprises">
                <Button size="lg" variant="outline-light" className="text-base px-8 h-14">
                  <Building2 className="w-5 h-5 mr-2" />
                  Formation entreprise
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap gap-8 mt-12">
              {[
                { n: `${trainings.length}+`, l: 'Formations' },
                { n: `${instructors.length}`, l: 'Instructeurs' },
                { n: '3', l: 'Modes' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <p className="text-3xl font-bold text-[#FFD537]">{s.n}</p>
                  <p className="text-white/50 text-sm">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container-app">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#F9B438] font-semibold mb-3">A PROPOS</p>
            <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-6">
              Bienvenue a PEY <span style={{color:'#F9B438'}}>a</span><span style={{color:'#F9B438'}}>CAD</span><span>emie</span>
            </h2>
            <div className="text-neutral-600 leading-relaxed space-y-4 text-left sm:text-center">
              {(settings?.intro_text || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="py-12" style={{background:'linear-gradient(135deg, rgba(255,213,55,0.08) 0%, rgba(249,180,56,0.08) 100%)'}}>
          <div className="container-app">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((cat, i) => (
                <Card key={cat.id} className="group hover:shadow-lg transition-all cursor-pointer border-none" onClick={() => { setFilter(cat.id); document.getElementById('catalogue')?.scrollIntoView({behavior:'smooth'}); }}>
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}>
                      <BookOpen className="w-7 h-7 text-[#1D1D1B]" />
                    </div>
                    <h3 className="font-heading font-semibold text-[#1D1D1B] mb-1">{cat.name}</h3>
                    <p className="text-neutral-500 text-sm">{cat.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Catalogue */}
      <section id="catalogue" className="section-padding bg-white">
        <div className="container-app">
          <div className="text-center mb-10">
            <p className="text-[#F9B438] font-semibold mb-2">CATALOGUE</p>
            <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-4">Nos formations</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">Des programmes structures concus par des experts pour developper vos competences techniques.</p>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}>Toutes</Button>
            {categories.map(c => (
              <Button key={c.id} variant={filter === c.id ? 'default' : 'outline'} size="sm" onClick={() => setFilter(c.id)}>{c.name}</Button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-12"><p className="text-neutral-500">Aucune formation disponible dans cette categorie.</p></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((t, i) => {
                const MIcon = modeIcon[t.mode] || MapPin;
                return (
                  <Card key={t.id} className="overflow-hidden group hover:shadow-xl transition-all animate-fadeIn" style={{animationDelay:`${i*80}ms`}} data-testid={`training-card-${t.slug}`}>
                    <div className="relative h-48 overflow-hidden" style={{background:'linear-gradient(135deg, #1D1D1B 0%, #2a2a28 100%)'}}>
                      {t.image_url && <img src={t.image_url} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                      {!t.image_url && <div className="w-full h-full flex items-center justify-center"><GraduationCap className="w-16 h-16 text-[#F9B438]/30" /></div>}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1D1D1B]/70 to-transparent" />
                      {t.promo_price && <Badge className="absolute top-3 right-3 bg-red-500 text-white">Promo</Badge>}
                      <Badge className="absolute top-3 left-3 text-[#1D1D1B]" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}>
                        <MIcon className="w-3 h-3 mr-1" />{modeLabel[t.mode]}
                      </Badge>
                    </div>
                    <CardContent className="p-5">
                      {t.category_name && <p className="text-xs text-[#F9B438] font-medium mb-1">{t.category_name}</p>}
                      <h3 className="font-heading font-semibold text-lg text-[#1D1D1B] mb-2 group-hover:text-[#F9B438] transition-colors">{t.title}</h3>
                      <p className="text-neutral-600 text-sm line-clamp-2 mb-4">{t.short_description}</p>
                      <div className="flex items-center gap-3 text-xs text-neutral-500 mb-4">
                        {t.duration && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{t.duration}</span>}
                        {t.instructors?.[0] && <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" />{t.instructors[0].name}</span>}
                        {t.upcoming_sessions > 0 && <span className="flex items-center gap-1 text-green-600"><Video className="w-3.5 h-3.5" />{t.upcoming_sessions} session(s)</span>}
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div>
                          {t.promo_price ? (
                            <><span className="text-lg font-bold text-[#1D1D1B]">{t.promo_price.toFixed(0)} $</span><span className="text-sm text-neutral-400 line-through ml-2">{t.price.toFixed(0)} $</span></>
                          ) : t.price > 0 ? (
                            <span className="text-lg font-bold text-[#1D1D1B]">{t.price.toFixed(0)} $</span>
                          ) : (
                            <span className="text-lg font-bold text-green-600">Gratuit</span>
                          )}
                        </div>
                        <Link to={`/academie/formation/${t.slug}`}>
                          <Button size="sm" data-testid={`training-details-${t.slug}`}>Details <ChevronRight className="w-4 h-4 ml-1" /></Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Process */}
      <section className="py-16" style={{background:'linear-gradient(135deg, #1D1D1B 0%, #2a2a28 100%)'}}>
        <div className="container-app">
          <div className="text-center mb-12">
            <p className="text-[#FFD537] font-semibold mb-2">COMMENT CA FONCTIONNE</p>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Processus d'inscription</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: BookOpen, t: 'Explorez', d: 'Parcourez notre catalogue et trouvez la formation adaptee.' },
              { icon: Target, t: 'Inscrivez-vous', d: 'Remplissez le formulaire avec vos informations.' },
              { icon: Zap, t: 'Payez en ligne', d: 'Reglez de facon securisee via Stripe.' },
              { icon: Award, t: 'Formez-vous', d: 'Recevez votre confirmation et participez.' },
            ].map((s, i) => (
              <div key={i} className="text-center relative">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}>
                  <s.icon className="w-8 h-8 text-[#1D1D1B]" />
                </div>
                <div className="absolute top-0 right-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/40 text-sm font-bold">{i+1}</div>
                <h3 className="font-heading font-semibold text-white mb-2">{s.t}</h3>
                <p className="text-white/50 text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise */}
      <section id="entreprises" className="section-padding bg-white">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F9B438] font-semibold mb-3">ENTREPRISES</p>
              <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-6">{settings?.enterprise_section_title || 'Formations pour entreprises'}</h2>
              <p className="text-neutral-600 leading-relaxed mb-6">{settings?.enterprise_section_text || 'PEY aCADemie offre des programmes sur mesure pour les entreprises souhaitant former leurs equipes.'}</p>
              <ul className="space-y-3 mb-8">
                {['Programmes personnalises selon vos besoins','Formation dans vos locaux ou a distance','Groupes de 5 a 25 participants','Suivi et evaluation des competences'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-neutral-700"><div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg, rgba(255,213,55,0.2) 0%, rgba(249,180,56,0.2) 100%)'}}><ChevronRight className="w-4 h-4 text-[#F9B438]" /></div>{item}</li>
                ))}
              </ul>
              <Link to="/contact"><Button size="lg"><Building2 className="w-4 h-4 mr-2" />Demander une soumission</Button></Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&fit=crop" alt="Formation entreprise" className="w-full h-80 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Instructors */}
      {instructors.length > 0 && (
        <section className="py-16" style={{background:'linear-gradient(135deg, rgba(255,213,55,0.06) 0%, rgba(249,180,56,0.06) 100%)'}}>
          <div className="container-app">
            <div className="text-center mb-10">
              <p className="text-[#F9B438] font-semibold mb-2">NOS FORMATEURS</p>
              <h2 className="font-heading text-3xl font-bold text-[#1D1D1B]">Experts et passionnes</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {instructors.map(inst => (
                <Card key={inst.id} className="overflow-hidden text-center">
                  <div className="h-48 bg-neutral-100">
                    {inst.photo_url ? <img src={inst.photo_url} alt={inst.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Users className="w-16 h-16 text-neutral-300" /></div>}
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-heading font-semibold text-[#1D1D1B]">{inst.name}</h3>
                    <p className="text-sm text-[#F9B438] mb-2">{inst.title}</p>
                    <div className="flex flex-wrap justify-center gap-1">{inst.expertise_areas?.map((a,i) => <Badge key={i} variant="outline" className="text-xs">{a}</Badge>)}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LMS Coming Soon */}
      <section className="py-16 bg-[#1D1D1B]">
        <div className="container-app text-center">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{background:'linear-gradient(135deg, rgba(255,213,55,0.2) 0%, rgba(249,180,56,0.2) 100%)'}}>
              <Video className="w-8 h-8 text-[#FFD537]" />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">Apprentissage en ligne - Bientot disponible</h2>
            <p className="text-white/60 mb-8">Nous preparons une plateforme d'apprentissage en ligne avec des formations video a la demande, des quiz interactifs et des certificats de completion.</p>
            <Badge className="text-[#1D1D1B] text-sm px-4 py-2" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}>En developpement</Badge>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{background:'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)'}}>
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1D1D1B] mb-4">Pret a developper vos competences?</h2>
          <p className="text-[#1D1D1B]/70 mb-8 max-w-xl mx-auto">Explorez notre catalogue et inscrivez-vous a la prochaine session de formation.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#catalogue"><Button size="lg" className="bg-[#1D1D1B] hover:bg-[#2a2a28] text-white"><GraduationCap className="w-4 h-4 mr-2" />Voir les formations</Button></a>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-[#1D1D1B] text-[#1D1D1B] hover:bg-[#1D1D1B] hover:text-white">Nous contacter</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AcademyPage;
